import { s, c, t, nextAppRouter } from '../../../../val.config';
import {
  singleMediaElementSchema,
  doubleMediaElementSchema,
  textElementSchema,
  linksElementSchema,
  textAndImageElementSchema,
  projectQuoteElementSchema,
  titleLinksElementSchema,
  videoElementSchema,
} from '../../../../schema/pageElements.val';

const projectSummary = s.object({
  client: s.string(),
  collaborators: s.string().nullable(),
  contributions: s.string().nullable(),
  period: s.string().nullable(),
  services: s.array(s.string()),
  description: s
    .richtext({
      bold: true,
      italic: true,
      lineThrough: true,
      h1: false,
      h2: false,
      h3: true,
      h4: true,
      h5: true,
      h6: true,
      ul: true,
      ol: true,
      a: true,
      img: true,
    })
    .nullable(),
});

const projectSchema = s
  .object({
    hero: s.object({
      title: s.string(),
      image: s.image().nullable(),
      shortDescription: s.string(),
      projectSummary: projectSummary,
      description: s.string(),
    }),
    thumbnail: s.image().nullable(),
    content: s.array(
      s.union(
        'type',
        textElementSchema,
        textAndImageElementSchema,
        projectQuoteElementSchema,
        videoElementSchema,
        titleLinksElementSchema,
        linksElementSchema,
        doubleMediaElementSchema,
        singleMediaElementSchema
      )
    ),
    colorScheme: s
      .object({
        backgroundColor: s.string().raw().nullable(),
        textColor: s.string().raw().nullable(),
      })
      .nullable(),
  })
  .preview(({ val }) => {
    return {
      title: val.hero.title,
      subtitle: val.hero.shortDescription,
      image: val.hero.image,
    };
  });
export type Project = t.inferSchema<typeof projectSchema>;
export const schema = s.router(nextAppRouter, projectSchema);

export default c.define('/app/(main)/arbeider/[slug]/page.val.ts', schema, {
  '/arbeider/ruter': {
    hero: {
      image: null,
      title: '',
      description: '',
      projectSummary: {
        client: '',
        period: null,
        services: [],
        description: [],
        collaborators: null,
        contributions: null,
      },
      shortDescription: '',
    },
    content: [],
    thumbnail: null,
    colorScheme: { textColor: null, backgroundColor: null },
  },
  '/arbeider/norwegian-subsea': {
    hero: {
      description: 'Norwegian Subsea er...',
      image: {
        path: '/public/val/norwegian_subsea_c0baa.jpg',
        width: 1350,
        height: 1350,
        mimeType: 'image/jpeg',
        alt: 'Norwegian Subsea',
      },
      projectSummary: {
        collaborators: null,
        client: 'Norwegian Subsea',
        contributions: 'Design og utvikling',
        services: ['tang', 'ting', 'en ting til'],
        description: [
          {
            tag: 'p',
            children: [
              'Norwegian Subsea ønsket å... Kanskje det skjer noe nå?',
            ],
          },
        ],
        period: null,
      },
      shortDescription: 'Ny konfigurator for røffe forhold',
      title: 'Norwegian Subsea',
    },
    thumbnail: {
      path: '/public/val/blank_b0035.webp',
      width: 1200,
      height: 675,
      mimeType: 'image/webp',
      alt: 'Blank logo',
    },
    colorScheme: {
      backgroundColor: '#000000',
      textColor: '#FFFFFF',
    },
    content: [
      {
        type: 'singleMediaElement',
        media: {
          type: 'image',
          content: {
            path: '/public/val/mru_f60e9.gif',
            width: 1280,
            height: 720,
            mimeType: 'image/gif',
            alt: 'MRU og applikasjon',
          },
        },
        keepAspectRatio: true,
        clickable: null,
      },
    ],
  },
  '/arbeider/aidn': {
    thumbnail: {
      path: '/public/val/mockuuups-imac-mockup-on-a-light-wooden-desk-with-soft-shadows_01dd1.jpg',
      width: 3939,
      height: 2699,
      mimeType: 'image/jpeg',
    },
    colorScheme: { backgroundColor: `#FDF2E6`, textColor: `#351B44` },
    content: [],
    hero: {
      description: `Nytt journalsystem for dokumentasjon og samarbeid i helsevesenet.`,
      image: {
        path: '/public/val/mockuuups-imac-mockup-on-a-light-wooden-desk-with-soft-shadows_01dd1.jpg',
        width: 3939,
        height: 2699,
        mimeType: 'image/jpeg',
        hotspot: { x: 0.005714285714285714, y: 0 },
      },
      projectSummary: {
        collaborators: null,
        client: `Aidn`,
        contributions: null,
        services: ['Design', 'Utvikling'],
        description: [
          {
            tag: 'p',
            children: [
              'Aidn har gått til kamp mot dagens utdaterte dokumentasjons- og rapporteringsverktøy i det kommunale helsevesenet og bygger derfor et helt nytt skybasert, smart pasientsystem som radikalt skal lette arbeidshverdagen for helseteamene rundt pasienten. Dette er den største investeringen innen e-helseløsninger kommunemarkedet noen gang har sett.',
            ],
          },
        ],
        period: null,
      },
      shortDescription: 'Aidn',
      title: 'Nytt journalsystem for dokumentasjon og samarbeid i helsevesenet',
    },
  },
  '/arbeider/bboy': {
    hero: {
      description: `Blanks korteste stunt-prosjekt; ett spill lagd på 3 uker i forbindelse med rapperen B-Boy Myhres albumslipp. `,
      image: {
        path: '/public/val/screenshot2025-06-20at10.46.251_adc51.jpg',
        width: 4182,
        height: 2664,
        mimeType: 'image/jpeg',
        hotspot: { x: 0.51, y: 0.4264596778340268 },
        alt: 'Skjermbilde fra spillet B-Boy Infinite',
      },
      projectSummary: {
        collaborators: null,
        client: `B-Boy Myhre og Little Big Sister`,
        contributions: `Design og utvikling`,
        services: [
          'Idéutvikling',
          'UX og UI',
          'Frontend-utvikling',
          'Backend-utvikling',
          'Produktstrategi',
        ],
        description: [
          {
            tag: 'p',
            children: [
              'I forbindelse med B-Boy Myhre sitt albumslipp lagde vi et runnerspill for å skape blest rundt albumet og hans kommende konserter. Spillet passer perfekt inn i B-Boy Myhres univers som enkelt kan beskrives som — "digitalt stusselig". ',
            ],
          },
        ],
        period: null,
      },
      shortDescription: `Blanks korteste stunt-prosjekt; ett spill lagd på 3 uker i forbindelse med rapperen B-Boy Myhres albumslipp. `,
      title: 'Utvikling av spill til B-Boy Myhres albumslipp',
    },
    thumbnail: {
      path: '/public/val/bboy_8d88f.webp',
      width: 2000,
      height: 1274,
      mimeType: 'image/webp',
      alt: 'Blank logo',
      hotspot: { x: 0.5054495912806539, y: 0.5605460802713822 },
    },
    colorScheme: { backgroundColor: `#282D2A`, textColor: `#71E25A` },
    content: [
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/2b5d8a89d9363be1dbf8016b959b43a61edbb594-1274x1092_2cb80.gif',
            width: 1274,
            height: 1092,
            mimeType: 'image/gif',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/b-boy_495c8.jpg',
            width: 1366,
            height: 2048,
            hotspot: { x: 0, y: 0.806 },
            mimeType: 'image/jpeg',
          },
        },
        title: null,
        text: null,
        layout: 'square-tall',
      },
      {
        type: 'projectQuoteElement',
        author: 'Rasmus Hungnes, Natt & Dag',
        quote: 'Her blir spillhistorien tatt til sin ytterste konsekvens.',
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'image',
          content: {
            path: '/public/val/dfd1831fcfa0cdb4d53bd060c6b6f2f8eae0e366-600x434_84395.gif',
            width: 600,
            height: 434,
            mimeType: 'image/gif',
          },
        },
        clickable: false,
        keepAspectRatio: false,
      },
      {
        text: [
          {
            tag: 'p',
            children: [
              'I spillet styrer man en protagonist ved hjelp av swipe eller piltaster, og målet er å fange så mange coins som mulig (som gir poeng av ulik verdi), og å styre unna blokker i ulik høyde. I bakgrunnen spilles B-Boy Myhres sang "1, 2 step" på repeat. Verdenen strekker seg også uendelig til sidene, best beskrevet av journalisten i ',
              {
                tag: 'a',
                href: 'https://nattogdag.no/2023/11/statsstottet-kritiker-om-b-boy-myhres-spill-spillhistorien-tatt-til-sin-ytterste-konsekven/',
                children: ['natt&dag'],
              },
              ' “Jeg trykket dobbelt til høyre tror jeg, holdt piltasten inne og forsvant ut mot verdens høyre ende, og jeg kunne se alt. Det hele. Det var ensomt."',
            ],
          },
        ],
        type: 'textElement',
        title: 'Infinite',
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/b-boymerch_aca18.png',
            width: 1882,
            height: 1768,
            mimeType: 'image/png',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/bboymerchbilde_ebfe7.webp',
            width: 1280,
            height: 1515,
            mimeType: 'image/webp',
          },
        },
        layout: 'square-tall',
        title: null,
        text: [
          {
            tag: 'p',
            children: [
              'I tillegg til spillet ble det også designet merch. B-Boy Myhre kjørte en instagram-konkurranse hvor de med høyest score i spillet vant hver sin B-Boy Infinite genser.',
            ],
          },
        ],
      },
      {
        type: 'videoElement',
        video: {
          path: '/public/val/e764772d1a513349ef71f0bdba02016045073aa8.mp4',
          mimeType: 'video/mp4',
        },
      },
      {
        type: 'linksElement',
        links: [
          {
            text: 'Spill B-Boy Infinite her',
            href: 'https://bboymyhre.neocities.org/',
          },
          {
            text: 'Les anmeldelsen i Natt og Dag',
            href: 'https://nattogdag.no/2023/11/statsstottet-kritiker-om-b-boy-myhres-spill-spillhistorien-tatt-til-sin-ytterste-konsekven/',
          },
          {
            text: 'Bloggpost om å lage et spill på 3 uker',
            href: 'https://blogg.blank.no/vi-mekket-et-spill-pÃ¥-3-uker-e82a681b072c',
          },
        ],
      },
    ],
  },
  '/arbeider/aneo': {
    thumbnail: {
      path: '/public/val/aneo_charger_hand_5f909.png',
      width: 4096,
      height: 2967,
      mimeType: 'image/png',
      alt: 'Telefon med ANEO-appen',
    },
    colorScheme: { backgroundColor: `#000000`, textColor: `#ffc328` },
    content: [
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/aneo_lader_9e75c.png',
            width: 684,
            height: 1105,
            mimeType: 'image/png',
            alt: 'Aneo lader',
          },
        },
        media2: {
          type: 'video',
          content: {
            path: '/public/val/jump_8a193.mp4',
            mimeType: 'video/mp4',
          },
          autoPlay: true,
        },
        title: null,
        text: [
          {
            tag: 'p',
            children: [
              'Blank har blant annet vært sentral i å implementere Aneos merkevareidentitet på tvers av web, ladebokser og markedsføringsmateriell.',
            ],
          },
        ],
        layout: 'tall-square',
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'image',
          content: {
            path: '/public/val/aneo_car_phones_8d42e.jpg',
            width: 4096,
            height: 1307,
            mimeType: 'image/jpeg',
            alt: 'Grafikk for Aneo',
          },
        },
        keepAspectRatio: true,
        clickable: null,
      },
      {
        type: 'textElement',
        title: 'Teknologi',
        text: [
          {
            tag: 'p',
            children: [
              'De fleste prosjekter holder seg på skjerm og server. Her handler det også om bokser, ledninger og protokoller, og om å få alt til å spille sammen.',
            ],
          },
          {
            tag: 'p',
            children: [
              { tag: 'br' },
              'I Aneo Mobility jobber vi tett på ladebokser fra blant annet Easee og Zaptec. Vi integrerer med proprietære HTTP API-er, lytter til OCPP-hendelser og -observasjoner via ladeoperatøren Emabler, og bygger en backend som holder det hele stabilt, uansett leverandør.',
            ],
          },
        ],
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/aneo_phone_in_car_d7929.png',
            width: 3763,
            height: 3850,
            mimeType: 'image/png',
            alt: 'Hånd som holder telefon med Aneo appen',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/aneo_app_icon_434c0.png',
            width: 2497,
            height: 1637,
            mimeType: 'image/png',
            alt: 'Telefon med Aneo appen "Min Lader" ikon',
          },
        },
        title: 'Aneo-appen og Min side',
        text: [
          {
            tag: 'p',
            children: [
              'Som en del av et tverrfaglig team har Blank spilt en viktig rolle i arbeidet med å utvikle Aneo-appen og Min side som lar kundene starte og stoppe lading, låse opp ladekabelen og administrere abonnement.',
            ],
          },
        ],
        layout: 'tall-square',
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'image',
          content: {
            path: '/public/val/aneo_app_my_page_mockups_0876a.jpg',
            width: 4096,
            height: 1587,
            mimeType: 'image/jpeg',
            alt: 'Aneo design mockups for app og min side',
          },
        },
        keepAspectRatio: null,
        clickable: null,
      },
      {
        type: 'projectQuoteElement',
        quote:
          'Siden 2022 har Blank hjulpet Aneo Mobility med de digitale løsningene våre. Sammen har vi løftet oss fra (bokstavelig talt) en fysisk server i en kjeller i Trøndelag, til en moderne Ladeplattform.',
        author: 'Mari Moxnes Karlsen, Chief Product Officer i Aneo',
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/aneo_web_components_065e9.jpg',
            width: 2550,
            height: 2550,
            mimeType: 'image/jpeg',
            alt: 'Web komponenter',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/aneo_web_phone_b4a15.jpg',
            width: 4096,
            height: 2732,
            mimeType: 'image/jpeg',
            alt: 'Hjemmesidene til Aneo vist på iPhone',
          },
        },
        title: 'Ny nettside på under tre måneder',
        text: [
          {
            tag: 'p',
            children: [
              'Blank har ledet design og utvikling av Aneo Mobilitys nye nettsider, fra arkitektur og oppsett i Next.js til integrasjon med Sanity og implementering av React-komponenter. Resultatet er en moderne, ytelsesorientert og lett vedlikeholdbar løsning for både det norske og svenske markedet levert på under tre måneder og innenfor budsjett.',
            ],
          },
        ],
        layout: 'square-tall',
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'image',
          content: {
            path: '/public/val/aneo_web_mac_18a4b.jpg',
            width: 3976,
            height: 2652,
            mimeType: 'image/jpeg',
            alt: 'Hjemmesidene til Aneo vist på Mac',
          },
        },
        keepAspectRatio: null,
        clickable: null,
      },
      {
        type: 'textElement',
        title: 'Andre ting vi har vært med på',
        text: [
          {
            tag: 'p',
            children: [
              'Blank har vært en nøkkelpartner i utviklingen av AneoPulse – et system som overvåker ladeinfrastrukturen til Aneos kunder i hele Norden. Da Aneo gikk over fra fastpris til spotprismodell i 2024/2025, bidro vi også til å migrere eksisterende kunder til en ny abonnementsstruktur.',
            ],
          },
        ],
      },
      {
        type: 'linksElement',
        links: [
          {
            text: 'Aneo Mobility sine nettsider',
            href: 'https://www.aneomobility.com',
          },
          {
            text: 'Video om AneoPulse',
            href: 'https://www.youtube.com/watch?v=INSajCF5iA4',
          },
        ],
      },
    ],
    hero: {
      description: `Aneo Mobility er Norges største leverandør av elbillading til borettslag og sameier.`,
      image: {
        path: '/public/val/aneo_charger_hand_5f909.png',
        width: 4096,
        height: 2967,
        mimeType: 'image/png',
        alt: 'Aneo lader',
      },
      projectSummary: {
        contributions: null,
        services: [
          'Idéutvikling',
          'Innsikt og analyse',
          'Tjenestedesign',
          'UX og UI',
          'Designsystem',
          'Frontend-utvikling',
          'Backend-utvikling',
          'Designsystem',
          'UX og UI',
          'Innsikt og analyse',
          'Arkitektur',
          'Tjenestedesign',
          'Innovasjonsprosesser',
          'Produkstrategi',
          'Prosjektledelse',
          'Produktledelse',
        ],
        client: 'Aneo Mobility',
        collaborators: null,
        description: [
          {
            tag: 'p',
            children: [
              'Aneo Mobility leverer smarte og effektive ladeløsninger til borettslag og sameier. ',
              { tag: 'br' },
              { tag: 'br' },
              'Som teknologipartner har Blank spilt en viktig rolle siden starten. Vi har bidratt med å bygge opp tech-stacken, og vært tett på utvikling, drift og videreutvikling av plattformene som håndterer ladeinfrastruktur og kundeadministrasjon.',
            ],
          },
        ],
        period: '2022 - i dag',
      },
      shortDescription: 'Fra oppstart til markedsleder i elbillading',
      title: 'Fra oppstart til markedsleder i elbillading',
    },
  },
  '/arbeider/hjemmelegene': {
    thumbnail: {
      path: '/public/val/hjemmelegene_8264a.webp',
      width: 2000,
      height: 1125,
      mimeType: 'image/webp',
      alt: 'Blank logo',
      hotspot: { x: 0.5490463215258855, y: 0.41383765279084506 },
    },
    colorScheme: { backgroundColor: `#f5f5f5`, textColor: `#000000` },
    content: [
      {
        type: 'textElement',
        text: [
          {
            tag: 'p',
            children: [
              'Etter etableringen har løsningene og behovene blitt stadig mer komplekse. Hjemmelegene ønsket derfor å ta grep for å skape en god, helhetlig opplevelse i nye kundereiser og på tvers av fysiske og digitale kontaktflater.  ',
            ],
          },
          {
            tag: 'p',
            children: [
              'Vi har i denne forbindelse bistått med å etablere og lede arbeidet med UX og tjenestedesign internt og jobbet integrert i Hjemmelegenes produktorganisasjon.  ',
            ],
          },
          {
            tag: 'p',
            children: [
              'Vi var involvert i både forbedringer og nyutvikling av Hjemmelegenes digitale interne og pasientrettede produkter.',
            ],
          },
        ],
        title: 'UX og tjenestedesign',
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/out-lege og pasient hjemme.avif',
            width: 1368,
            height: 1368,
            mimeType: 'image/avif',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/out-mobil mockup hjemmelegene.avif',
            width: 1368,
            height: 2210,
            mimeType: 'image/avif',
          },
        },
        layout: 'square-tall',
        title: 'Digitale konsultasjoner',
        text: [
          {
            tag: 'p',
            children: [
              'Vi designet en ny løsning for digitale konsultasjoner over video, chat og telefon, en tjeneste både for pasienter og for legene som behandler dem.',
            ],
          },
        ],
      },
      {
        type: 'singleMediaElement',
        clickable: false,
        keepAspectRatio: false,
        media: {
          type: 'image',
          content: {
            path: '/public/val/out-mockups mørk bakgrunn.avif',
            width: 2784,
            height: 1350,
            mimeType: 'image/avif',
          },
        },
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/out-klinikk i butikk.avif',
            width: 1368,
            height: 2210,
            mimeType: 'image/avif',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/out-klinikk i butikk2.avif',
            width: 1368,
            height: 1368,
            mimeType: 'image/avif',
          },
        },
        layout: 'tall-square',
        title: 'Klinikk i butikk',
        text: [
          {
            tag: 'p',
            children: [
              'Vi jobbet også med tjenestedesign og brukeropplevelse for Hjemmelegenes nye satsningsområde innenfor medtail, sykepleierklinikker som tilbyr helsetjenester i kombinasjon med detaljhandel.',
            ],
          },
        ],
      },
    ],
    hero: {
      description: `Design for Hjemmelegene, en helhetlig tjeneste med mål om å skape et nært og godt møte mellom pasient og helsepersonell.`,
      image: {
        path: '/public/val/hjemmelegene-v2-komprimert_15023.webp',
        width: 3456,
        height: 2234,
        mimeType: 'image/webp',
        alt: 'En mann med et barn som sitter i sofaen',
        hotspot: { x: 0.44666666666666666, y: 0.5440247432758449 },
      },
      projectSummary: {
        collaborators: null,
        description: [
          {
            tag: 'p',
            children: [
              ' Hjemmelegene er en helhetlig helsetjeneste med mål om å skape et nært og godt møte mellom pasient og helsepersonell. I 2017 etablerte de seg med å tilby legebesøk hjemme, støttet av en digital plattform. I dag tilbyr de flere helsetjenester både hjemme, på arbeidsplasser og klinikker.',
            ],
          },
        ],
        client: `Hjemmelegene`,
        contributions: null,
        services: ['Innsikt og analyse', 'Tjenestedesign', 'UX og UI'],
        period: null,
      },
      shortDescription: 'Et nært og godt møte mellom pasient og helsepersonell',
      title: 'Hjemmelegene',
    },
  },
  '/arbeider/finn': {
    thumbnail: {
      path: '/public/val/diagnoseporten_bfe6d.webp',
      alt: 'Laptop med Finn.no åpen',
      width: 6250,
      height: 3886,
      mimeType: 'image/webp',
      hotspot: { x: 0.3814713896457766, y: 0.3062740734400411 },
    },
    colorScheme: { backgroundColor: `#13233B`, textColor: `#FFFFFF` },
    content: [
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/utsnitt3_d717f.jpg',
            width: 6390,
            height: 6390,
            mimeType: 'image/jpeg',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/kattbruktbilfinn_26772.jpg',
            width: 1368,
            height: 2210,
            mimeType: 'image/jpeg',
          },
        },
        layout: 'square-tall',
        title: null,
        text: null,
      },
      {
        type: 'textElement',
        text: [
          {
            tag: 'p',
            children: [
              'Det har blitt vanskeligere å tjene penger i bruktbilbransjen de siste årene. Markedet er i endring, det er små marginer og det finnes begrensede kilder til innsikt for å ta gode strategiske beslutninger.',
              { tag: 'br' },
              { tag: 'br' },
              'Ved å gjøre ',
              { tag: 'a', href: '/arbeider/finn', children: ['FINN.no'] },
              '-data forståelig og lett tilgjengelig, hjelper Diagnoseporten bilforhandlere med å ta datadrevne og lønnsomme beslutninger i arbeidshverdagen.',
            ],
          },
          { tag: 'p', children: [] },
          {
            tag: 'p',
            children: [
              'Eksempler på innsikt bilforhandlerne kan finne er; hvor lenge ligger en bil ute på FINN før den blir solgt? Hvilke biler bør man satse på? Og hvilken pris kan man ta?',
            ],
          },
        ],
        title: 'Bakgrunn og løsning',
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'image',
          content: {
            path: '/public/val/diagnoseportenlaptop2_58fa1.jpg',
            width: 2400,
            height: 931,
            mimeType: 'image/jpeg',
          },
        },
        keepAspectRatio: null,
        clickable: null,
      },
      {
        type: 'projectQuoteElement',
        author: 'Magnus Groseth, daglig leder hos bilforhandleren KBIL',
        quote:
          'Det ligger ekstremt mye kunnskap i dette verktøyet. Rett og slett et genialt produkt som gjør det enda enklere å ta inn riktige biler. [..] Alle som jobber hos KBIL bruker det hver eneste dag!',
      },
    ],
    hero: {
      description: `Diagnoseporten fra FINN — en unik oversikt over dagens bruktbilmarked og hvordan det utvikler seg.`,
      image: {
        path: '/public/val/diagnoseporten_bfe6d.webp',
        width: 6250,
        height: 3886,
        mimeType: 'image/webp',
        hotspot: { x: 0.43, y: 0.40208942899711 },
      },
      projectSummary: {
        description: [
          {
            tag: 'p',
            children: [
              'Diagnoseporten er et abonnementsbasert innsiktsverktøy fram ',
              { tag: 'a', href: '/arbeider/finn', children: ['FINN.no'] },
              ' for å hjelpe bruktbilforhandlere å ta mer datadrevne beslutninger i et marked preget av små marginer og rask endring. ',
              { tag: 'br' },
              { tag: 'br' },
              'Blank har bistått i utviklingen av tjenesten helt fra oppstarten som en intern start-up, og har vært med på å kartlegge behov, utvikle konsept og iterere i tett samarbeid med FINN og bruktbilbransjen. I dag er det et større team som drifter og itererer på verktøyet samtidig som det rulles ut i markedet.',
            ],
          },
        ],
        client: `FINN.no`,
        collaborators: `Team Innsikt som produkt og UX-avdelingen i FINN.no`,
        contributions: `Laget en tjeneste for bilbransjen med innsikt om bruktbilmarkedet`,
        services: [
          'Idéutvikling',
          'Innsikt og analyse',
          'Tjenestedesign',
          'UX og UI',
          'Designsystem',
        ],
        period: null,
      },
      shortDescription:
        'Diagnoseporten — oversikt over dagens bruktbilmarked og hvordan det utvikler seg',
      title:
        'Diagnoseporten — oversikt over dagens bruktbilmarked og hvordan det utvikler seg',
    },
  },
  '/arbeider/ice': {
    thumbnail: {
      path: '/public/val/icemockupmobilprvoss_3a11b.webp',
      alt: 'Blank logo',
      width: 7070,
      height: 4716,
      mimeType: 'image/webp',
    },
    colorScheme: { backgroundColor: `#585449`, textColor: `#ffffff` },
    content: [
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/fontice_6f7f3.png',
            width: 2583,
            height: 2583,
            mimeType: 'image/png',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/icepcmedforside_e0350.jpg',
            width: 1368,
            height: 2210,
            mimeType: 'image/jpeg',
          },
        },
        layout: 'square-tall',
        title: null,
        text: [
          {
            tag: 'p',
            children: [
              'Vi har vært med og utviklet Ice sin merkevare, strategisk og praktisk, og levert løsninger på tvers av flater og kanaler.',
            ],
          },
        ],
      },
      {
        type: 'textElement',
        text: [
          {
            tag: 'p',
            children: [
              'Vi har levert et bredt spekter av tjenester, inkludert midlertidig ledelse av utviklingsavdelingen, ledelse av designfunksjonen over flere år, samt praktisk utviklings- og designarbeid. ',
              { tag: 'br' },
              { tag: 'br' },
              'Blant nøkkelprosjektene finner vi Ice sin rebranding og identitet, Ice-appen, Min Side for privat- og bedriftskunder, kundesentrert tjenestedesign, backend-løsninger, GDPR-etterlevelse og en PoS-løsning.',
            ],
          },
        ],
        title: 'Blank sin rolle',
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/diegoutsnitt_0319c.jpg',
            width: 3219,
            height: 3219,
            mimeType: 'image/jpeg',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/ibutikken_31091.jpg',
            width: 2052,
            height: 3316,
            mimeType: 'image/jpeg',
          },
        },
        layout: 'square-tall',
        title: 'Diego',
        text: [
          {
            tag: 'p',
            children: [
              'Når en selger i en av ice sine egne butikker eller hos forhandlere som Power eller Elkjøp selger en telefon med et ice-abonnement, gjennomføres salget i Diego. Blank har stått for UX og UI-design.',
            ],
          },
        ],
      },
      {
        type: 'doubleMediaElement',

        media1: {
          type: 'image',
          content: {
            path: '/public/val/kundeservicearbeider_9b6c5.jpg',
            width: 1368,
            height: 2211,
            mimeType: 'image/jpeg',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/mannyutsnitt_cb063.png',
            width: 2469,
            height: 2469,
            mimeType: 'image/png',
          },
        },
        layout: 'tall-square',
        title: 'Manny',
        text: [
          {
            tag: 'p',
            children: [
              'Ice har vunnet mange priser for sin fremragende kundeservice. Verktøyet kunderserviceagentene bruker heter Manny. Blank var med fra start.',
            ],
          },
        ],
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/mobilbytte_e3bc3.jpg',
            width: 1368,
            height: 1369,
            mimeType: 'image/jpeg',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/icebedrift2_325a4.jpg',
            width: 2166,
            height: 3246,
            mimeType: 'image/jpeg',
          },
        },
        layout: 'square-tall',
        title: 'IceBedrift, Selvbetjeningsløsninger og mye mer',
        text: [
          {
            tag: 'p',
            children: [
              'I de siste ti årene har vi også redesignet IceBedrift, bidratt i utviklingen av Mobilbytte og mye mer. Nå redesigner vi Ice-appen og Mine Sider for privatkundene.',
            ],
          },
        ],
      },
    ],
    hero: {
      description: 'Fra nisjeaktør til mobilgigant',
      image: {
        path: '/public/val/icemockupmobilprvoss_3a11b.webp',
        width: 7070,
        height: 4716,
        mimeType: 'image/webp',
        hotspot: { x: 0.48428571428571426, y: 0.4647380661569093 },
      },
      projectSummary: {
        client: `ice`,
        collaborators: `ice, diverse folk fra Blank.`,
        services: [
          'Tjenestedesign',
          'UX og UI',
          'Designsystem',
          'Frontend-utvikling',
          'UX og UI',
          'Produktledelse',
          'Designsystem',
          'Tjenestedesign',
          'Backend-utvikling',
          'Arkitektur',
          'Produktstrategi',
          'Prosjektledelse',
        ],
        contributions: `Design, innsikt, analyse, konsept, testing og utvikling`,
        description: [
          {
            tag: 'p',
            children: [
              'Helt siden Ice lanserte mobiltelefoni og startet utbyggingen av mobilnettet sitt, har de hatt ett mål for øyet: å utfordre og forbedre det norske mobilmarkedet. Gjennom de første syv årene som mobiloperatør gikk de fra én kunde og én mobilmast til over 700 000 kunder. Blank har vært med på reisen.',
            ],
          },
        ],
        period: '2015 - i dag',
      },
      shortDescription: 'Fra nisjeaktør til mobilgigant',
      title: 'Fra nisjeaktør til mobilgigant',
    },
  },
  '/arbeider/morgenlevering': {
    thumbnail: {
      path: '/public/val/morgenlevering_e3d3d.webp',
      width: 2000,
      height: 1304,
      mimeType: 'image/webp',
      alt: 'Frokostbord med bakst fra Morgenlevering',
      hotspot: { x: 0.4904632152588556, y: 0.5262241488025001 },
    },
    colorScheme: { backgroundColor: `#fffdf5`, textColor: `#1b3732` },
    content: [
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/logomorgenlevering_3eaae.avif',
            width: 1368,
            height: 1368,
            mimeType: 'image/avif',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/bildefrokost_5b279.avif',
            width: 1368,
            height: 1368,
            mimeType: 'image/avif',
          },
        },
        title: null,
        text: null,
        layout: 'square-square',
      },
      {
        type: 'textElement',
        text: [
          {
            tag: 'p',
            children: [
              'Det er en terskel å bestille hjemlevering til noen andre enn seg selv. Det innebærer at mottaker må være hjemme innenfor et gitt tidspunkt. Her skiller Morgenlevering seg ut, de benytter seg av avisbudene for å levere ferskt bakst og gaver sammen med avisa, helt frem til dørmatta. ',
              { tag: 'br' },
              { tag: 'br' },
              'Dette gjør Morgenlevering til en perfekt tjeneste for gaver – har du bestilt en Morgenlevering trenger du ikke å vite om gavemottakeren er hjemme mellom klokka 14–19. Så lenge de våkner hjemme, våkner de til en melding om en gave på dørmatten. Litt julenissefaktor.',
            ],
          },
        ],
        title: `Unik markedsposisjon`,
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/bildebakst_dd300.avif',
            width: 1368,
            height: 1368,
            mimeType: 'image/avif',
            hotspot: { x: 0.4925373134328358, y: 0.548 },
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/skjermbilde_a0282.avif',
            width: 2700,
            height: 2700,
            mimeType: 'image/avif',
          },
        },
        title: null,
        text: null,
        layout: 'square-square',
      },
      {
        type: 'textElement',
        text: [
          {
            tag: 'p',
            children: [
              'Den første jobben var å automatisere en del interne arbeidsoppgaver som tok mye tid fra enkelte ansatte – flere personer satt oppe sent hver eneste dag for å manuelt “stenge butikken” for bestillinger når Morgenlevering ikke hadde flere frokoster er å sende ut. Dette ordnet vi – så i dag kan alle ta helg uten bekymring.',
              { tag: 'br' },
              { tag: 'br' },
              'Vi benyttet muligheten til å freshe opp designet og rydde opp i en del problemer knyttet til tilgjenglighet og brukeropplevelse av tjenesten, både på løsningen ut mot kunde, men også internt på strategisk og distribusjonsnivå.',
              { tag: 'br' },
              { tag: 'br' },
              'Videre flyttet vi Morgenlevering over til Sanity, og ryddet opp i både design og kode for å bygge felles komponenter som er gjenbrukbare.',
            ],
          },
        ],
        title: 'Blank sin rolle',
      },
    ],
    hero: {
      description: `Fersk frokost levert på døren før du står opp.`,
      image: {
        path: '/public/val/covermorgenlevering_504aa.avif',
        width: 2784,
        height: 1132,
        mimeType: 'image/avif',
      },
      projectSummary: {
        collaborators: null,
        client: `Morgenlevering.no`,
        contributions: `Utvikling og UX-, UI og tjenestedesign.`,
        services: [
          'Frontend-utvikling',
          'Backend-utvikling',
          'Skytjenester',
          'UX og UI',
        ],
        description: [
          {
            tag: 'p',
            children: [
              {
                tag: 'a',
                href: '/arbeider/morgenlevering',
                children: ['Morgenlevering.no'],
              },
              ' gjør det enkelt å overraske noen med frokost, blomster eller gaver på døra før de våkner.',
            ],
          },
          {
            tag: 'p',
            children: [
              'Vi har utviklet på ',
              {
                tag: 'a',
                href: '/arbeider/morgenlevering',
                children: ['Morgenlevering.no'],
              },
              ' siden 2017 og levert skymigrering til AWS og Kubernetes, utvikling med React og Kotlin, samt UX og visuelt design.',
            ],
          },
        ],
        period: null,
      },
      shortDescription: 'Fersk frokost levert på døren før du står opp',
      title: 'Fersk frokost levert på døren før du står opp',
    },
  },
  '/arbeider/remarkable': {
    thumbnail: {
      path: '/public/val/remarkable_6b146.webp',
      width: 4389,
      height: 2925,
      mimeType: 'image/webp',
      alt: 'Blank logo',
      hotspot: { x: 0.4986376021798365, y: 0.45655874132736685 },
    },
    colorScheme: { backgroundColor: `#f2eeea`, textColor: `#000000` },
    content: [
      {
        type: 'projectQuoteElement',
        author: `Øyvind`,
        quote: `reMarkable gir deg mulighet til å organisere og arkivere notater og tegninger digitalt samtidig som du får friheten til å fokusere på kreativiteten og ideene — i stedet for å bli distrahert av teknologi.`,
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'image',
          content: {
            path: '/public/val/images/905287bc44898e96b9d9364f12a8f84ce385bf92-6240x4160.webp',
            width: 6240,
            height: 4160,
            mimeType: 'image/webp',
          },
        },
        clickable: null,
        keepAspectRatio: false,
      },
      {
        type: 'textElement',
        text: [
          {
            tag: 'p',
            children: [
              'Siden 2016 har vi hatt et tett samarbeid med reMarkable. Det første vi jobbet med hos reMarkable var design av brukergrensesnittet på tegne- og skrivebrettet. Her jobbet vi med både grunnleggende interaksjonsdesign i operativsystemet, ikonografi, navigasjon og menystruktur. ',
            ],
          },
          {
            tag: 'p',
            children: [
              'Vi har også bidratt i arbeidet med håndskriftkonvertering, maler og utvikling av tilhørende tjenester i reMarkable sin mobil-app.',
            ],
          },
        ],
        title: `Dette har vi jobbet med `,
      },
      {
        type: 'doubleMediaElement',
        layout: 'tall-square',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/images/1bee88afe3844358cba29ae41753869d843ad358-6240x4160.webp',
            width: 6240,
            height: 4160,
            mimeType: 'image/webp',
            hotspot: { x: 0.55858310626703, y: 0.428 },
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/images/ef970d9d541bbf390a41b393727d08909b30dbbe-6240x4160.webp',
            width: 6240,
            height: 4160,
            mimeType: 'image/webp',
            hotspot: { x: 0.5040871934604905, y: 0.428 },
          },
        },
        title: null,
        text: [
          {
            tag: 'p',
            children: [
              'Blank har også hatt flere utviklere og designere i ulike team på reMarkable som har jobbet på store deler av reMarkables nettside og nettsidens tilhørende tjenester. Her har vi jobbet med alt fra kampanjer og cms (content management systems), til støtte for ulike land og valutaer og integrasjon mot betalingsplattformer.',
              'Etter lansering av reMarkable 2 og abonnementløsningen Connect har vi bidratt med salg og selvbetjeningsløsninger. I hovedsak på ',
              {
                tag: 'a',
                href: 'http://remarkable.com',
                children: ['remarkable.com'],
              },
              ' og ',
              {
                tag: 'a',
                href: 'http://my.remarkable.com',
                children: ['my.remarkable.com'],
              },
              '.   ',
            ],
          },
          {
            tag: 'p',
            children: [
              'På my.remarkable.com kan brukere laste ned apper og koble seg opp mot ulike tjenester i økosystemet til reMarkable, samt håndtere abonnementet.',
            ],
          },
        ],
      },
    ],
    hero: {
      description: `Digitalt skrive- og tegnebrett som gir følelsen av å skrive på vanlig papir`,
      image: {
        path: '/public/val/images/2cef3d87fca4e4817d4b0f385ced96b3cb9742b5-6240x4160.webp',
        width: 6240,
        height: 4160,
        mimeType: 'image/webp',
      },
      projectSummary: {
        collaborators: null,
        client: `reMarkable`,
        services: [
          'Tjenestedesign',
          'UX og UI',
          'Frontend-utvikling',
          'UX og UI',
          'Produktledelse',
          'Backend-utvikling',
          'Skytjenester',
          'Tjenestedesign',
          'Arkitektur',
          'Smidig metodikk',
          'Produktstrategi',
        ],
        contributions: `Design og utvikling`,
        description: [
          {
            tag: 'p',
            children: [
              `For få år siden var reMarkable en liten start-up i Oslo i en ikke-eksisterende nisje i markedet. I dag har reMarkable utviklet seg til et selskap som hevder seg på det globale markedet og hisser på seg store aktører som Amazon og Lenovo. Til tross for flere konkurrerende produkter beholder reMarkable posisjonen som det ledende produktet i markedet.`,
            ],
          },
        ],
        period: null,
      },
      shortDescription: `Digitalt skrive- og tegnebrett som gir følelsen av å skrive på vanlig papir`,
      title: 'Følelsen av å skrive på vanlig papir',
    },
  },
  '/arbeider/reisen-til-spektrum': {
    thumbnail: {
      path: '/public/val/sondre_e6559.webp',
      width: 4695,
      height: 7287,
      mimeType: 'image/webp',
      alt: 'Portrett av Sondre Justad',
      hotspot: { x: 0.5817438692098093, y: 0.4025 },
    },
    colorScheme: { backgroundColor: `#FAFCFF`, textColor: `#000000` },
    content: [
      {
        author: `Sondre Justad (Aftenposten Magasinet)`,
        quote: `Dette skal være en måte vi blir kjent med hverandre på før vi møtes i Oslo Spektrum.`,
        type: 'projectQuoteElement',
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'video',
          content: { path: '/public/val/justad.mp4', mimeType: 'video/mp4' },
          autoPlay: false,
        },
        keepAspectRatio: true,
        clickable: false,
      },
      {
        type: 'textElement',
        text: [
          {
            tag: 'p',
            children: [
              'Det hele starter med Victoria, Sondres største fan, som får en personlig invitasjon og blir bedt om å invitere fem andre. Invitasjonene sprer seg videre – og etter 199 dager er konserten utsolgt, helt uten media eller tradisjonelle kanaler.',
            ],
          },
        ],
        title: `Hviskeleken`,
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/spektrum1.avif',
            width: 1152,
            height: 1152,
            mimeType: 'image/avif',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/spektrum2.avif',
            width: 1368,
            height: 2210,
            mimeType: 'image/avif',
          },
        },
        layout: 'square-tall',
        title: null,
        text: [
          {
            tag: 'p',
            children: [
              'Med AMOI kan man fylle opp handlekurven med kvalitetsvarer fra byens beste butikker og produsenter; bukett fra en blomsterbutikk, øl fra bryggeri, kjøtt fra slakteren, brød fra bakeriet, og kaffe rett fra brenneriet. Amoi skaper denne synergien, som gir verdi både for leverandørene og for kunden.',
            ],
          },
        ],
      },
      {
        type: 'textElement',
        text: [
          {
            tag: 'p',
            children: [
              'Reisen til Spektrum foregår i et digitalt kart fra Sondre sin egen leilighet på Tøyen, gjennom Grønland og til Oslo Spektrum. Her kan de som har billett få tilgang til eksklusivt innhold, og bli invitert til arrangementer både fysisk og digitalt. Justad har blant annet sluppet sanger fra sitt nye album i løsningen før offisiell lansering.',
            ],
          },
        ],
        title: `Fra Tøyen til Spektrum`,
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'image',
          content: {
            path: '/public/val/spektrum3.webp',
            height: 1396,
            width: 3600,
            mimeType: 'image/webp',
          },
        },
        clickable: false,
        keepAspectRatio: false,
      },
      {
        type: 'projectQuoteElement',
        author: `Juryen, Visueltprisen`,
        quote: `Forventningen og oppbyggingen til konserten blir forsterket gjennom en integrert digital opplevelse på tvers av flater`,
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'image',
          content: {
            path: '/public/val/images/3064b5e40967f1d1bea1d8dd95efa41ca26c96b0-1920x1281.webp',
            width: 1920,
            height: 1281,
            mimeType: 'image/webp',
            hotspot: { x: 0.5133333333333333, y: 0.24980225004985676 },
          },
        },
        clickable: null,
        keepAspectRatio: false,
      },
      {
        type: 'linksElement',
        links: [
          {
            href: 'https://www.aftenposten.no/amagasinet/i/z7yRyw/sondre-justad-aapnet-seg-da-alt-stengte',
            text: 'Artikkel i Aftenposten',
          },
          {
            href: 'https://730.no/sondre-justad-solgte-ut-hemmelig-oslo-spektrum-konsert/',
            text: 'Artikkel i 730',
          },
          {
            href: 'https://www.lofot-tidende.no/sondre-har-solgt-ut-oslo-spektrum-maten-han-gjorde-det-pa-var-ganske-spesiell-er-det-en-drom-eller-er-det-pa-ekte/s/5-28-351032',
            text: 'Artikkel i Lofot-Tidende',
          },
          {
            href: 'https://www.lofotposten.no/na-gjor-sondre-noe-han-aldri-har-gjort-for-jeg-drives-av-a-komme-tett-pa-andre-mennesker/s/5-29-759369',
            text: 'Artikkel i Lofotposten',
          },
          {
            href: 'https://tv.nrk.no/serie/dagsrevyen/202111/NNFA19110821/avspiller#t=32m55s',
            text: 'Innslag på NRK Dagsrevyen',
          },
          {
            href: 'https://radio.nrk.no/serie/nyhetsmorgen/sesong/202111/NPUB32022121#t=1h15m18s',
            text: 'Innslag på NRK Nyhetsmorgen',
          },
          {
            href: 'https://www.fremover.no/ingen-fikk-vite-om-konserten-men-sondre-justad-solgte-den-helt-ut/s/5-17-916706',
            text: 'Artikkel i Fremover',
          },
        ],
      },
    ],
    hero: {
      description: `Hvordan Sondre Justad solgte ut Oslo Spektrum i all hemmelighet.`,
      image: {
        path: '/public/val/justad_dcd40.avif',
        width: 2560,
        height: 1440,
        mimeType: 'image/avif',
        hotspot: { x: 0.4714285714285714, y: 0.5511111111111111 },
      },
      projectSummary: {
        client: `Sondre Justad`,
        collaborators: `Sondre Justad, Sesong 1, Beyond Management & ANTI`,
        contributions: `Konseptutvikling, design og utvikling`,
        services: [
          'Idéutvikling',
          'UX og UI',
          'Frontend-utvikling',
          'Backend-utvikling',
          'Produktstrategi',
        ],
        description: [
          {
            tag: 'p',
            children: [
              'Sondre Justad skulle spille i Oslo Spektrum for første gang, og ønsket å bygge fellesskap med fansen. ',
              { tag: 'br' },
              { tag: 'br' },
              'Sammen utviklet vi “Reisen til Spektrum” – en ny måte å kjøpe billett på, som skaper personlig nærhet og en sterkere tilhørighet mellom artist og publikum.',
            ],
          },
        ],
        period: null,
      },
      shortDescription: `Hvordan Sondre Justad solgte ut Oslo Spektrum i all hemmelighet.`,
      title: `Reisen til Spektrum`,
    },
  },
  '/arbeider/amoi': {
    thumbnail: {
      path: '/public/val/forsidebilde_629a9.jpg',
      alt: 'Skjermbilde av forsiden til Amoi.no',
      width: 2518,
      height: 3332,
      mimeType: 'image/jpeg',
      hotspot: { x: 0.4904632152588556, y: 0.61 },
    },
    colorScheme: { backgroundColor: `#0C3A45`, textColor: `#E9F2F1` },
    content: [
      {
        type: 'videoElement',
        video: { path: '/public/val/AMOI_Hero.mp4', mimeType: 'video/mp4' },
      },
      {
        type: 'projectQuoteElement',
        author: 'Gulltaggen, Jury',
        quote:
          'Amoi har klart å skape en varm følelse av den lokale nabobutikken med god kvalitet.\n\nAlt oser kvalitet, det å oppnå dette i en stor og tung organisasjon er nytenkende, modig og ikke minst svært imponerende.',
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/amoimobiler_f1784.jpg',
            width: 1368,
            height: 1369,
            mimeType: 'image/jpeg',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/amoikokk_db787.jpg',
            width: 1368,
            height: 2211,
            mimeType: 'image/jpeg',
          },
        },
        layout: 'square-tall',
        title: null,
        text: [
          {
            tag: 'p',
            children: [
              'Med AMOI kan man fylle opp handlekurven med kvalitetsvarer fra byens beste butikker og produsenter; bukett fra en blomsterbutikk, øl fra bryggeri, kjøtt fra slakteren, brød fra bakeriet, og kaffe rett fra brenneriet. Amoi skaper denne synergien, som gir verdi både for leverandørene og for kunden.',
            ],
          },
        ],
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'image',
          content: {
            path: '/public/val/amoihjemmesideogmobilkort_f42f4.jpg',
            width: 2400,
            height: 896,
            mimeType: 'image/jpeg',
          },
        },
        clickable: null,
        keepAspectRatio: false,
      },

      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/holdermobilamoi_4320f_19aca.webp',
            width: 1368,
            height: 2210,
            mimeType: 'image/webp',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/rectangleillustrations_2f124_61ee8.webp',
            width: 1368,
            height: 1368,
            mimeType: 'image/webp',
          },
        },
        layout: 'tall-square',
        title: null,
        text: null,
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'image',
          content: {
            path: '/public/val/leggetilprodukt_02882.jpg',
            width: 2784,
            height: 1302,
            mimeType: 'image/jpeg',
          },
        },
        clickable: null,
        keepAspectRatio: true,
      },
    ],
    hero: {
      description: `AMOI er en samlet markedsplass med tilbud fra ulike lokale butikker og tjenester i Oslo. Du bestiller fra flere butikker samtidig og får det lynraskt levert hjem til deg.`,
      image: {
        path: '/public/val/forsidebilde_629a9.jpg',
        width: 2518,
        height: 3332,
        mimeType: 'image/jpeg',
        hotspot: { x: 0.5, y: 0.706 },
        alt: 'Markedsplass med tilbud håndplukket fra byens beste butikker',
      },
      projectSummary: {
        description: [
          {
            tag: 'p',
            children: [
              'Amoi gjør det enkelt å handle kvalitetsvarer fra Oslos beste butikker og få dem levert hjem samme dag. Kunden bestiller på ',
              { tag: 'a', href: '/arbeider/amoi', children: ['amoi.no'] },
              ', Posten henter varene hos hver enkelt leverandør og leverer dem samlet hjem. Her kan du få alt fra blomster og brød til kaffe, kjøtt og øl fra favorittbutikkene i byen.',
              { tag: 'br' },
              { tag: 'br' },
              'Blank har bidratt med merkevare, design/UX og plattformutvikling.',
            ],
          },
        ],
        client: `Posten, AMOI`,
        collaborators: `Postens innovasjonsmiljø`,
        contributions: `Brand- og designsystem, UX-, UI- og tjenestedesign, IA, art direction av film og fotografi, illustrasjon, grafisk design`,
        services: [
          'Idéutvikling',
          'Innsikt og analyse',
          'Frontend-utvikling',
          'Backend-utvikling',
          'Tjenestedesign',
          'UX og UI',
          'Designsystem',
          'Arkitektur',
          'Innovasjonsprosesser',
          'Produkstrategi',
          'Prosjektledelse',
          'Produktledelse',
        ],
        period: null,
      },
      shortDescription:
        'Markedsplass med tilbud håndplukket fra byens beste butikker',
      title: 'Amoi',
    },
  },
  '/arbeider/sas-skien': {
    thumbnail: {
      path: '/public/val/sas_54766.webp',
      width: 4695,
      height: 3129,
      mimeType: 'image/webp',
      alt: 'Digitalt bilde av en UFO',
      hotspot: { x: 0.5122615803814714, y: 0.49470203798482304 },
    },
    colorScheme: { backgroundColor: `#000000`, textColor: `#ffffff` },
    content: [
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/images/1543dbed7fb374a578423123ac14c1cb7028b81f-320x543.gif',
            width: 320,
            height: 543,
            mimeType: 'image/gif',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/images/f4c363ea1e24c29d7628b02d6097383a7228ab57-320x546.gif',
            width: 320,
            height: 546,
            mimeType: 'image/gif',
          },
        },
        title: null,
        text: null,
        layout: 'square-tall',
      },
      {
        type: 'textElement',
        text: [
          {
            tag: 'p',
            children: [
              'sasskien.no bygger videre på 90-tallsestetikken i SPSP-universet, men i takt med at spørsmålene blir mer og mer kryptiske knekker løsningen, fasaden går gradvis i oppløsning og du blir trukket ned i en alternativ og mørkere virkelighet under overflaten.',
            ],
          },
          {
            tag: 'p',
            children: [
              'Det er en løsning som skaper engasjement og som oppleves som litt merkelig, litt skummel og som trigger både forventning og undring hos fansen.',
            ],
          },
        ],
        title: `SAS Pussy SAS Plus`,
      },
      {
        type: 'textElement',
        text: [
          {
            tag: 'p',
            children: [
              'Det ble sendt inn over 10 000 søknader via sasskien.no de første 24 timene etter lansering.  ',
            ],
          },
          {
            tag: 'p',
            children: ['Løsningen fikk Gull i Gulltaggen og diplom i Visuelt.'],
          },
        ],
        title: `10 000 søknader`,
      },
      // TODO: fire bilder her
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/images/6c8e99a7f4a848003658e8a15dbd431cd14b33d2-320x546.gif',
            width: 320,
            height: 546,
            mimeType: 'image/gif',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/images/ba947d1e18c102604f7c7a96dac73600aed999d6-320x554.gif',
            width: 320,
            height: 554,
            mimeType: 'image/gif',
          },
        },
        title: null,
        text: null,
        layout: 'tall-square',
      },
    ],
    hero: {
      description: `SAS Skien er en del av prosjektet Sas Plus/Sas Pussy (SPSP), Karpe sitt hittil største prosjekt med et tidsspenn på over tre år.`,
      image: {
        path: '/public/val/images/49c207c16c48ecc4100b25f9d8ac262f0c85d437-1200x630.webp',
        width: 1200,
        height: 630,
        mimeType: 'image/webp',
        hotspot: { x: 0.24714285714285714, y: 0.3836734693877551 },
      },
      projectSummary: {
        description: [
          {
            tag: 'p',
            children: [
              'I 2020 skulle Karpe holde fem forestillinger i Festiviteten i Skien for 100 spesielt inviterte fans.',
            ],
          },
          { tag: 'p', children: [] },
          {
            tag: 'p',
            children: [
              'For å bli plukket ut til SAS Skien måtte du melde deg på gjennom ',
              {
                tag: 'a',
                href: '/arbeider/sas-skien',
                children: ['sasskien.no'],
              },
              '. En digital opplevelse utviklet sammen med Karpe og Little Big Sister.',
            ],
          },
        ],
        client: `Karpe og Little Big Sister`,
        collaborators: `Blank, Also Known As og Karpe`,
        contributions: '',
        services: [
          'Idéutvikling',
          'UX og UI',
          'Frontend-utvikling',
          'Backend-utvikling',
          'Produktstrategi',
        ],
        period: null,
      },
      shortDescription: 'Et ganske uvanlig søknadsskjema',
      title: 'SAS Skien',
    },
  },
  '/arbeider/krf': {
    hero: {
      title: 'Visuell identitet for Kristelig Folkeparti',
      image: {
        path: '/public/val/casephoto_46ad0.jpg',
        width: 4182,
        height: 2727,
        mimeType: 'image/jpeg',
        alt: 'Kristelig Folkeparti-logo med et fjellandskap i bakgrunnen',
        hotspot: { x: 0.5142857142857142, y: 0.5082703336521593 },
      },
      shortDescription: 'Visuell identitet for Kristelig Folkeparti',
      projectSummary: {
        client: 'Kristelig Folkeparti',
        collaborators: null,
        contributions: 'Visuell identitet',
        period: '2024-2025',
        services: ['Logo', 'Typografi', 'Farger', 'Maler', 'Illustrasjoner'],
        description: [
          {
            tag: 'p',
            children: [
              'Kristelig Folkeparti (KrF) er et kristendemokratisk parti i Norge, bygget på menneskeverd, nestekjærlighet og forvaltningsansvar. I forkant av stortingsvalget 2025 så KrF behovet for å modernisere sin visuelle identitet som del av en politisk oppstandelse.',
              { tag: 'br' },
              { tag: 'br' },
              'Leveransen inkluderte ny logo, fargepalett, typografi, illustrasjoner og maler. Partiet ønsket å ta eierskap til gult for å styrke sin posisjon, og bli mer synlige og moderne for å nå ut til unge voksne. Samtidig var det viktig å videreføre partiets historie, varme og verdier for å bevare tilliten blant eldre velgere.',
            ],
          },
        ],
      },
      description: '',
    },
    thumbnail: {
      path: '/public/val/krf_8c3af.webp',
      width: 2000,
      height: 1304,
      mimeType: 'image/webp',
      hotspot: { x: 0.49591280653950953, y: 0.5224977285585364 },
    },
    content: [
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/imagewithcaption_a8bd6.jpg',
            width: 2049,
            height: 3315,
            mimeType: 'image/jpeg',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/image(1)_b78a6.jpg',
            width: 2051,
            height: 2052,
            mimeType: 'image/jpeg',
          },
        },
        title: null,
        text: [
          {
            tag: 'p',
            children: [
              'Typografien består av SangBleu Kingdom i kursiv, med sin sofistikerte og moderne serif-stil, og DM Sans, som tilfører enkelhet og balanse. Sammen danner de et fleksibelt typografisk system som fungerer på tvers av ulike flater og behov.',
            ],
          },
        ],
        layout: 'tall-square',
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'image',
          content: {
            path: '/public/val/photosection_0c547.jpg',
            width: 3585,
            height: 1395,
            mimeType: 'image/jpeg',
            hotspot: { x: 0.4513450292397661, y: 0.49121153123612393 },
          },
        },
        clickable: null,
        keepAspectRatio: false,
      },
      {
        type: 'textElement',
        title: 'Detaljene',
        text: [
          {
            tag: 'p',
            children: [
              'KrFs nye visuelle identitet er en moderne tolkning av partiets tradisjonelle verdier. Logoen kombinerer klassisk typografi, som formidler historie, tillit og stabilitet, med moderne detaljer og skarpe kanter. Det fremoverlente designet signaliserer også bevegelse og progresjon. Gultoner står i sentrum av fargepaletten og skaper en gjenkjennelig identitet som fremhever energien, optimismen og varmen i partiet.',
            ],
          },
        ],
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'video',
          content: { path: '/public/val/krf.mp4', mimeType: 'video/mp4' },
          autoPlay: null,
        },
        clickable: false,
        keepAspectRatio: false,
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/imagewithcaption(1)_2fb83.jpg',
            width: 2052,
            height: 2052,
            mimeType: 'image/jpeg',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/imagewithcaption(2)_eb212.jpg',
            width: 2052,
            height: 3314,
            mimeType: 'image/jpeg',
          },
        },
        title: null,
        text: null,
        layout: 'square-tall',
      },
    ],
    colorScheme: { backgroundColor: null, textColor: null },
  },
  '/arbeider/wewillwrite': {
    hero: {
      title: 'Hvordan gjør man skriving gøy for barn?',
      image: {
        path: '/public/val/Cover photo.avif',
        width: 2784,
        height: 1132,
        mimeType: 'image/avif',
        hotspot: { x: 0.17857142857142858, y: 0.4672678332966115 },
      },
      shortDescription: 'Hvordan gjør du skriving gøy igjen?',
      projectSummary: {
        client: 'WeWillWrite',
        collaborators: null,
        contributions: null,
        period: '2023 - i dag',
        services: [
          'Frontend-utvikling',
          'Backend-utvikling',
          'Skytjenester',
          'UX og UI',
        ],
        description: [
          {
            tag: 'p',
            children: [
              'WeWillWrite er et nettbasert skrivespill som gjør skriving gøy igjen for elever – gjennom lagbaserte skriveutfordringer som bygger mestring og historiefortelling. Blank kom inn i prosjektet høsten 2023 og har siden jobbet tett med teamet for å videreutvikle plattformen fra betaversjon til lansering på SXSW EDU i Austin.',
            ],
          },
        ],
      },
      description: '',
    },
    thumbnail: {
      path: '/public/val/Cover photo.avif',
      width: 2784,
      height: 1132,
      mimeType: 'image/avif',
      hotspot: { x: 0.17857142857142858, y: 0.4672678332966115 },
    },
    content: [
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/task example.avif',
            width: 2700,
            height: 2700,
            mimeType: 'image/avif',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/image tall.avif',
            width: 1368,
            height: 2210,
            mimeType: 'image/avif',
          },
        },
        title: null,
        text: null,
        layout: 'square-tall',
      },
      {
        type: 'textElement',
        title: 'Blank sin rolle',
        text: [
          {
            tag: 'p',
            children: [
              'Vi har blant annet optimalisert brukeropplevelsen og grensesnittet, restrukturert profilhåndtering og API-er, samt forenklet prosesser og forbedret kvaliteten på tvers av flere ledd. Løsningen er utviklet med SvelteKit, Node.js, Tailwind, PostgreSQL, TypeScript, Stripe og Vercel.',
            ],
          },
        ],
      },
      {
        type: 'singleMediaElement',
        clickable: false,
        keepAspectRatio: false,
        media: {
          type: 'image',
          content: {
            path: '/public/val/mockup slideshow.avif',
            width: 2784,
            height: 1350,
            mimeType: 'image/avif',
          },
        },
      },
      {
        type: 'projectQuoteElement',
        author: 'Daniel Senn, Grunnlegger av WeWillWrite',
        quote:
          'Uten Blank hadde vi ikke klart å lansere så raskt og håndtere den kraftige veksten.',
      },
      {
        type: 'singleMediaElement',
        clickable: false,
        keepAspectRatio: true,
        media: {
          type: 'image',
          content: {
            path: '/public/val/image 11.avif',
            width: 1392,
            height: 860,
            mimeType: 'image/avif',
          },
        },
      },
    ],
    colorScheme: { backgroundColor: null, textColor: null },
  },
  '/arbeider/bi': {
    hero: {
      image: {
        path: '/public/val/_e206364_530d6.jpg',
        width: 6720,
        height: 4480,
        mimeType: 'image/jpeg',
        alt: 'Skilt med BI sin logo på campus',
      },
      title: 'En enklere studiehverdag for Norges ledende handelshøyskole',
      description: '',
      projectSummary: {
        client: 'Handelshøyskolen BI',
        period: '2023-nå',
        services: [
          'Innsikt og analyse',
          'UX og UI',
          'designsystem',
          'frontend-utvikling',
          'backend-utvikling',
          'kunstig intelligens',
          'arkitektur',
        ],
        description: [
          {
            tag: 'p',
            children: [
              'Siden 2023 har vi vært en del av BIs digitale satsing og utviklet løsninger som forenkler studiehverdagen for over 20 000 studenter. Med kunstig intelligens, brukervennlige tjenester og et helhetlig designsystem har vi gjort det enklere for studenter å finne frem, og for BI å utvikle sine digital tjenester.',
            ],
          },
        ],
        collaborators: null,
        contributions: null,
      },
      shortDescription:
        'Enklere studiehverdag for Norges ledende handelshøyskole',
    },
    content: [
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'video',
          content: {
            path: '/public/val/Designsystem_Spacing_Animasjon.mp4',
            mimeType: 'video/mp4',
          },
          autoPlay: true,
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/components_d0114.png',
            width: 2160,
            height: 2160,
            mimeType: 'image/png',
            alt: 'Utsnitt fra komponentbibliotek',
          },
        },
        title: null,
        text: null,
        layout: 'tall-square',
      },
      {
        type: 'textElement',
        title: 'Grunnmuren i BIs  digitale tjenester',
        text: [
          {
            tag: 'p',
            children: [
              'Frem til 2023 manglet BI et felles designsystem, noe som førte til variasjoner i både utseende og funksjonalitet på tvers av de digitale tjenestene. Det ga en lite helhetlig opplevelse for studentene og gjorde design og utvikling unødvendig tidkrevende.',
              { tag: 'br' },
              { tag: 'br' },
              'Sammen med BI har vi bygget et nytt designsystem i Figma og Storybook. Ved å kartlegge behov og sette tydelige retningslinjer for farger, typografi, ikoner og komponenter, har vi lagt grunnlaget for en mer enhetlig og brukervennlig digital opplevelse. Systemet bidrar til smidigere samarbeid mellom designere og utviklere, slik at de kan bruke mer tid til å løse viktige brukerutfordringer.',
            ],
          },
        ],
      },
      {
        type: 'singleMediaElement',
        media: {
          type: 'video',
          content: {
            path: '/public/val/designsystemtypographyanimation_9a800.mp4',
            mimeType: 'video/mp4',
          },
          autoPlay: true,
        },
        keepAspectRatio: true,
        clickable: false,
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'image',
          content: {
            path: '/public/val/designsystemfigmamockup_0cd52.webp',
            width: 4800,
            height: 3200,
            mimeType: 'image/webp',
            alt: 'PC med skisse av BI sine nettsider',
          },
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/designsystemfargepalettbilde_c7545.png',
            width: 1800,
            height: 2880,
            mimeType: 'image/png',
            alt: 'BI sin fargepalett',
          },
        },
        title: null,
        text: null,
        layout: 'square-tall',
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'video',
          content: {
            path: '/public/val/Designsystem_Ikoner_Animasjon_1.mp4',
            mimeType: 'video/mp4',
          },
          autoPlay: true,
        },
        media2: {
          type: 'video',
          content: {
            path: '/public/val/Designsystem_Ikoner_Animasjon_2.mp4',
            mimeType: 'video/mp4',
          },
          autoPlay: true,
        },
        title: null,
        text: null,
        layout: 'square-square',
      },
      {
        type: 'textElement',
        title: 'Enklere studievalg med hybridsøk',
        text: [
          {
            tag: 'p',
            children: [
              'Tidligere slet mange studenter med å finne relevante studier og kurs på BI sine nettsider. Søket ga ofte dårlige treff, og manglet muligheter for å filtrere etter behov som studieform, fagområde eller studiested.',
              { tag: 'br' },
              { tag: 'br' },
              'Sammen med BI har vi redesignet søkeopplevelsen og implementert semantisk søk med Azure AI. Ved å kombinere nøkkelord og vektorsøk får brukerne nå mer relevante treff, også når søket ikke matcher eksakte ord. Resultatet gir studentene en mer intuitiv og tilgjengelig måte å utforske BIs utdanningstilbud på.',
            ],
          },
        ],
      },
      {
        type: 'doubleMediaElement',
        media1: {
          type: 'video',
          content: {
            path: '/public/val/teknologiskanimasjon_4827d.mp4',
            mimeType: 'video/mp4',
          },
          autoPlay: true,
        },
        media2: {
          type: 'image',
          content: {
            path: '/public/val/sokmockupcropped_f88e8.png',
            alt: 'Student som søker etter studier og kurs',
            width: 2732,
            height: 2732,
            mimeType: 'image/png',
          },
        },
        title: null,
        text: null,
        layout: 'tall-square',
      },
    ],
    thumbnail: {
      path: '/public/val/_e206364_530d6.jpg',
      width: 6720,
      height: 4480,
      mimeType: 'image/jpeg',
      alt: 'Skilt med BI sin logo på campus',
    },
    colorScheme: null,
  },
  '/arbeider/Autodesk': {
    hero: {
      image: null,
      title: '',
      description: '',
      projectSummary: {
        client: '',
        period: null,
        services: [],
        description: [],
        collaborators: null,
        contributions: null,
      },
      shortDescription: '',
    },
    content: [],
    thumbnail: null,
    colorScheme: { textColor: null, backgroundColor: null },
  },
  '/arbeider/vend': {
    hero: {
      image: {
        path: '/public/val/vend_f7d05.jpg',
        width: 6138,
        height: 4092,
        mimeType: 'image/jpeg',
      },
      title: ' AI-drevne produkter',
      description: '',
      projectSummary: {
        client: 'Vend',
        period: null,
        services: [],
        description: [
          {
            tag: 'p',
            children: [
              'Department of AI (dAI) er en uavhengig innovasjonsavdeling i Vend som bygger AI-drevne produkter for plattformer som blant annet FINN i Norge og Blocket i Sverige. Målet er å gjøre brukerreiser mer intuitive og utvikle plattformene fra søkeverktøy til smarte assistenter.',
              { tag: 'br' },
            ],
          },
          {
            tag: 'p',
            children: [
              'FINN Søkelys er en ny tjeneste for baklengs rekruttering: AI-en matcher bedrifters ansettelsesbehov mot en CV-database, slik at rekrutterere kan invitere aktuelle kandidater direkte til intervju. Jobbsøkere får mer relevante jobbmuligheter basert på erfaring og ønsker for neste jobb, uten å skrive søknadsbrev.',
            ],
          },
        ],
        collaborators: null,
        contributions: null,
      },
      shortDescription: '',
    },
    content: [],
    thumbnail: {
      path: '/public/val/vend_f7d05.jpg',
      width: 6138,
      height: 4092,
      mimeType: 'image/jpeg',
    },
    colorScheme: { textColor: null, backgroundColor: null },
  },
  '/arbeider/BBL': {
    hero: {
      image: null,
      title: 'Regnskap og boligforvaltning for boretslag',
      description: '',
      projectSummary: {
        client: 'BBL',
        period: null,
        services: ['Innsikt og analyse', 'UX og UI', 'Designsystem'],
        description: [
          {
            tag: 'p',
            children: [
              'BBL Pivotal utvikler løsninger for boligbyggelag, styremedlemmer og beboere som til sammen forvalter 290 000 boliger og 580 000 medlemmer.',
            ],
          },
          {
            tag: 'p',
            children: [
              'Vi bidrar med design til Hårfagre NXT, plattformen rådgivere og regnskapsførere i boligbyggelag bruker til å føre regnskap for styrene, samt hjelpe til med drift og oppfølging.',
            ],
          },
        ],
        collaborators: null,
        contributions: null,
      },
      shortDescription: 'Regnskap og boligforvaltning for boretslag',
    },
    content: [],
    thumbnail: null,
    colorScheme: { textColor: null, backgroundColor: null },
  },
  '/arbeider/raalabs': {
    hero: {
      image: {
        path: '/public/val/raalabs_60eb6.jpg',
        width: 1695,
        height: 1128,
        mimeType: 'image/jpeg',
      },
      title: 'Datainfrastruktur til verdensledende skipsredere',
      description: '',
      projectSummary: {
        client: '',
        period: null,
        services: [],
        description: [
          {
            tag: 'p',
            children: [
              'Raa Labs leverer datainfrastruktur til verdensledende skipsredere som Wallenius Wilhelmsen og Seaspan. De samler høyfrekvente driftsdata fra systemer om bord, standardiserer formater og tilgjengeliggjør berikede data på tvers av flåter.',
            ],
          },
        ],
        collaborators: null,
        contributions: null,
      },
      shortDescription: '',
    },
    content: [],
    thumbnail: {
      path: '/public/val/raalabs_60eb6.jpg',
      width: 1695,
      height: 1128,
      mimeType: 'image/jpeg',
    },
    colorScheme: { textColor: null, backgroundColor: null },
  },
  '/arbeider/skatteetaten': {
    hero: {
      image: null,
      title: '',
      description: '',
      projectSummary: {
        client: '',
        period: null,
        services: [],
        description: [],
        collaborators: null,
        contributions: null,
      },
      shortDescription: '',
    },
    content: [],
    thumbnail: null,
    colorScheme: { textColor: null, backgroundColor: null },
  },
  '/arbeider/reddel': {
    hero: {
      image: null,
      title: '',
      description: '',
      projectSummary: {
        client: '',
        period: null,
        services: [],
        description: [],
        collaborators: null,
        contributions: null,
      },
      shortDescription: '',
    },
    content: [],
    thumbnail: null,
    colorScheme: { textColor: null, backgroundColor: null },
  },
  '/arbeider/gjensidige': {
    hero: {
      image: null,
      title: '',
      description: '',
      projectSummary: {
        client: '',
        period: null,
        services: [],
        description: [],
        collaborators: null,
        contributions: null,
      },
      shortDescription: '',
    },
    content: [],
    thumbnail: null,
    colorScheme: { textColor: null, backgroundColor: null },
  },
  '/arbeider/stortinget': {
    hero: {
      image: null,
      title: '',
      description: '',
      projectSummary: {
        client: '',
        period: null,
        services: [],
        description: [],
        collaborators: null,
        contributions: null,
      },
      shortDescription: '',
    },
    content: [],
    thumbnail: null,
    colorScheme: { textColor: null, backgroundColor: null },
  },
  '/arbeider/NAV': {
    hero: {
      image: null,
      title: '',
      description: '',
      projectSummary: {
        client: '',
        period: null,
        services: [],
        description: [],
        collaborators: null,
        contributions: null,
      },
      shortDescription: '',
    },
    content: [],
    thumbnail: null,
    colorScheme: { textColor: null, backgroundColor: null },
  },
  '/arbeider/hafslund': {
    hero: {
      image: {
        path: '/public/val/hafslund_f2f7f.jpg',
        width: 5760,
        height: 3240,
        mimeType: 'image/jpeg',
      },
      title: 'Energi på lag med naturen',
      description: '',
      projectSummary: {
        client: 'Hafslund',
        period: null,
        services: [],
        description: [],
        collaborators: null,
        contributions: null,
      },
      shortDescription:
        'Hafslund er Norges nest største kraftprodusent og jobber mot et smart og fullelektrifisert nullutslippssamfunn. Blank bidrar til drift av Hafslunds 83 vannkraftverk ved å skape verktøy for smidige og robuste prosesser i en kompleks og sikkerhetskritisk arbeidshverdag.',
    },
    content: [],
    thumbnail: {
      path: '/public/val/hafslund_f2f7f.jpg',
      width: 5760,
      height: 3240,
      mimeType: 'image/jpeg',
    },
    colorScheme: { textColor: '#351B44', backgroundColor: '#FDF2E6' },
  },
});
